import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  fetchSettingBookEmail,
  updateSettingBookEmail,
} from "redux/actions/settingBookEmail";
import CommonTable, { CommonTableProps } from "components/CommonTable";
import SettingBookEmailRes from "types/res/settingBookEmail/SettingBookEmailRes";
import styled from "styled-components";

import { FormType } from "components/FormField";
import { fetchShops } from "redux/actions/shop";
import UpdateSettingBookEmailReq from "types/req/settingBookEmail/UpdateSettingBookEmailReq";
import { Box } from "@material-ui/core";

const SettingBookEmail: React.FC = () => {
  const { t } = useTranslation("bookEmail");
  const dispatch = useDispatch();
  const companyId = useSelector((state) => state.account.staff.companyId);
  const settingBookEmails = useSelector((state) => state.settingBookEmail);
  useEffect(() => {
    dispatch(fetchShops(companyId));
    dispatch(fetchSettingBookEmail(companyId));
  }, [dispatch, companyId]);

  const headers: { key: keyof SettingBookEmailRes; label: string }[] = [
    { key: "isOrderDateEnabled", label: t("headers.orderDate") },
    { key: "isOrderCreatedByEnabled", label: t("headers.orderCreatedBy") },
    { key: "isOrderUpdatedByEnabled", label: t("headers.orderUpdatedBy") },
    { key: "isGuestCategoryEnabled", label: t("headers.guestCategory") },
    { key: "isGuestNameEnabled", label: t("headers.guestName") },
    { key: "isGuestTelEnabled", label: t("headers.guestTel") },
    { key: "isGuestMemoEnabled", label: t("headers.guestMemo") },
    { key: "isShopNameEnabled", label: t("headers.shopName") },
    { key: "isCastNameEnabled", label: t("headers.castName") },
    { key: "isCastDesignateCountEnabled", label: t("headers.castDesignateCount") },
    { key: "isCastClassNameEnabled", label: t("headers.castClassName") },
    { key: "isCastDesignateNameEnabled", label: t("headers.castDesignateName") },
    { key: "isCourseNameEnabled", label: t("headers.courseName") },
    { key: "isAdditionalTimeEnabled", label: t("headers.additionalTime") },
    { key: "isTotalTimeEnabled", label: t("headers.totalTime") },
    { key: "isDiscountNameEnabled", label: t("headers.discountName") },
    { key: "isOptionNameEnabled", label: t("headers.optionName") },
    { key: "isCosplayNameEnabled", label: t("headers.cosplayName") },
    { key: "isAreaEnabled", label: t("headers.area") },
    { key: "isHotelEnabled", label: t("headers.hotel") },
    { key: "isOrderAddressEnabled", label: t("headers.orderAddress") },
    { key: "isOrderDepartureTimeEnabled", label: t("headers.orderDepartureTime") },
    { key: "isOrderPlanInTimeEnabled", label: t("headers.orderPlanInTime") },
    { key: "isOrderActualInTimeEnabled", label: t("headers.orderActualInTime") },
    { key: "isOrderActualEndTimeEnabled", label: t("headers.orderActualEndTime") },
    { key: "isOrderMemoEnabled", label: t("headers.orderMemo") },
    { key: "isOrderPaymentTypeEnabled", label: t("headers.orderPaymentType") },
    { key: "isCarInfoEnabled", label: t("headers.carInfo") },
    { key: "shopId", label: t("headers.shop") },
  ];

  const toggleOptions = [
    { id: "0", name: t("toggle.off") },
    { id: "1", name: t("toggle.on") },
  ];

  const forms = [
    {
      label: t("headers.orderDate"),
      key: "isOrderDateEnabled",
      type: FormType.Boolean,
      options: toggleOptions,
    },
    {
      label: t("headers.orderCreatedBy"),
      key: "isOrderCreatedByEnabled",
      type: FormType.Boolean,
      options: toggleOptions,
    },
    {
      label: t("headers.orderUpdatedBy"),
      key: "isOrderUpdatedByEnabled",
      type: FormType.Boolean,
      options: toggleOptions,
    },
    {
      label: t("headers.guestCategory"),
      key: "isGuestCategoryEnabled",
      type: FormType.Boolean,
      options: toggleOptions,
    },
    {
      label: t("headers.guestName"),
      key: "isGuestNameEnabled",
      type: FormType.Boolean,
      options: toggleOptions,
    },
    {
      label: t("headers.guestTel"),
      key: "isGuestTelEnabled",
      type: FormType.Boolean,
      options: toggleOptions,
    },
    {
      label: t("headers.guestMemo"),
      key: "isGuestMemoEnabled",
      type: FormType.Boolean,
      options: toggleOptions,
    },
    {
      label: t("headers.shopName"),
      key: "isShopNameEnabled",
      type: FormType.Boolean,
      options: toggleOptions,
    },
    {
      label: t("headers.castName"),
      key: "isCastNameEnabled",
      type: FormType.Boolean,
      options: toggleOptions,
    },
    {
      label: t("headers.castDesignateCount"),
      key: "isCastDesignateCountEnabled",
      type: FormType.Boolean,
      options: toggleOptions,
    },
    {
      label: t("headers.castClassName"),
      key: "isCastClassNameEnabled",
      type: FormType.Boolean,
      options: toggleOptions,
    },
    {
      label: t("headers.castDesignateName"),
      key: "isCastDesignateNameEnabled",
      type: FormType.Boolean,
      options: toggleOptions,
    },
    {
      label: t("headers.courseName"),
      key: "isCourseNameEnabled",
      type: FormType.Boolean,
      options: toggleOptions,
    },
    {
      label: t("headers.additionalTime"),
      key: "isAdditionalTimeEnabled",
      type: FormType.Boolean,
      options: toggleOptions,
    },
    {
      label: t("headers.totalTime"),
      key: "isTotalTimeEnabled",
      type: FormType.Boolean,
      options: toggleOptions,
    },
    {
      label: t("headers.discountName"),
      key: "isDiscountNameEnabled",
      type: FormType.Boolean,
      options: toggleOptions,
    },
    {
      label: t("headers.optionName"),
      key: "isOptionNameEnabled",
      type: FormType.Boolean,
      options: toggleOptions,
    },
    {
      label: t("headers.cosplayName"),
      key: "isCosplayNameEnabled",
      type: FormType.Boolean,
      options: toggleOptions,
    },
    {
      label: t("headers.area"),
      key: "isAreaEnabled",
      type: FormType.Boolean,
      options: toggleOptions,
    },
    {
      label: t("headers.hotel"),
      key: "isHotelEnabled",
      type: FormType.Boolean,
      options: toggleOptions,
    },
    {
      label: t("headers.orderAddress"),
      key: "isOrderAddressEnabled",
      type: FormType.Boolean,
      options: toggleOptions,
    },
    {
      label: t("headers.orderDepartureTime"),
      key: "isOrderDepartureTimeEnabled",
      type: FormType.Boolean,
      options: toggleOptions,
    },
    {
      label: t("headers.orderPlanInTime"),
      key: "isOrderPlanInTimeEnabled",
      type: FormType.Boolean,
      options: toggleOptions,
    },
    {
      label: t("headers.orderActualInTime"),
      key: "isOrderActualInTimeEnabled",
      type: FormType.Boolean,
      options: toggleOptions,
    },
    {
      label: t("headers.orderActualEndTime"),
      key: "isOrderActualEndTimeEnabled",
      type: FormType.Boolean,
      options: toggleOptions,
    },
    {
      label: t("headers.orderMemo"),
      key: "isOrderMemoEnabled",
      type: FormType.Boolean,
      options: toggleOptions,
    },
    {
      label: t("headers.orderPaymentType"),
      key: "isOrderPaymentTypeEnabled",
      type: FormType.Boolean,
      options: toggleOptions,
    },
    {
      label: t("headers.carInfo"),
      key: "isCarInfoEnabled",
      type: FormType.Boolean,
      options: toggleOptions,
    },
  ];

  return (
    <>
      <Box maxWidth={"100vw"}>
        <SettingBookEmailTable
          fullWidth
          title={t("titles.bookEmail")}
          rows={headers}
          data={settingBookEmails}
          formId="settingBookEmailForm"
          forms={forms}
          updateType={UpdateSettingBookEmailReq}
          addFunc={(formData) => updateSettingBookEmail(companyId, formData)}
          updateFunc={(formData) => updateSettingBookEmail(companyId, formData)}
          deleteFunc={(formData) => updateSettingBookEmail(companyId, formData)}
          invisibleDeleteIcon={true}
          values={[
            (s) => (s.isOrderDateEnabled ? "○" : "×"),
            (s) => (s.isOrderCreatedByEnabled ? "○" : "×"),
            (s) => (s.isOrderUpdatedByEnabled ? "○" : "×"),
            (s) => (s.isGuestCategoryEnabled ? "○" : "×"),
            (s) => (s.isGuestNameEnabled ? "○" : "×"),
            (s) => (s.isGuestTelEnabled ? "○" : "×"),
            (s) => (s.isGuestMemoEnabled ? "○" : "×"),
            (s) => (s.isShopNameEnabled ? "○" : "×"),
            (s) => (s.isCastNameEnabled ? "○" : "×"),
            (s) => (s.isCastDesignateCountEnabled ? "○" : "×"),
            (s) => (s.isCastClassNameEnabled ? "○" : "×"),
            (s) => (s.isCastDesignateNameEnabled ? "○" : "×"),
            (s) => (s.isCourseNameEnabled ? "○" : "×"),
            (s) => (s.isAdditionalTimeEnabled ? "○" : "×"),
            (s) => (s.isTotalTimeEnabled ? "○" : "×"),
            (s) => (s.isDiscountNameEnabled ? "○" : "×"),
            (s) => (s.isOptionNameEnabled ? "○" : "×"),
            (s) => (s.isCosplayNameEnabled ? "○" : "×"),
            (s) => (s.isAreaEnabled ? "○" : "×"),
            (s) => (s.isHotelEnabled ? "○" : "×"),
            (s) => (s.isOrderAddressEnabled ? "○" : "×"),
            (s) => (s.isOrderDepartureTimeEnabled ? "○" : "×"),
            (s) => (s.isOrderPlanInTimeEnabled ? "○" : "×"),
            (s) => (s.isOrderActualInTimeEnabled ? "○" : "×"),
            (s) => (s.isOrderActualEndTimeEnabled ? "○" : "×"),
            (s) => (s.isOrderMemoEnabled ? "○" : "×"),
            (s) => (s.isOrderPaymentTypeEnabled ? "○" : "×"),
            (s) => (s.isCarInfoEnabled ? "○" : "×"),
            (s) => s?.shop?.name || t("values.notSet"),
          ]}
        />
      </Box>
    </>
  );
};

const SettingBookEmailTable = styled<
  React.FC<
    CommonTableProps<
      SettingBookEmailRes,
      UpdateSettingBookEmailReq,
      UpdateSettingBookEmailReq
    >
  >
>(CommonTable)`
  margin-top: 24px;
`;

export default SettingBookEmail;
